// Pure transformation helpers used by `avatar init` to derive project names,
// workspace names from git URLs, and build the ScaffoldVariables struct for
// template rendering. Extracted from init.ts for testability — no IO,
// no prompts, no git calls.

import type { ScaffoldVariables } from "../lib/project-tree-scaffolder.js";
import { readCliVersion } from "../lib/read-cli-version-from-package-json.js";

// v1.4.0: Render GitNexus section content cho CLAUDE.md template.
// Trả empty string nếu user skip GitNexus → template không có section dangling.
// Strong instruction Q4 confirmed: "soft" wording — Claude tự action khi thấy
// staleness hint từ MCP resource.
export function buildGitnexusSection(gitnexusReady: boolean): string {
  if (!gitnexusReady) return "";
  return `
### 🧠 CODEBASE INTELLIGENCE — GitNexus

Workspace có GitNexus index tại \`.gitnexus/\` cung cấp architectural awareness
cho Claude Code (impact analysis, call chains, blast radius).

**Hướng dẫn cho Claude:**

- Khi cần hiểu codebase architecture (vd: trước refactor, debug call chain) →
  query GitNexus MCP resource \`gitnexus://repo/<name>/context\`
- Khi resource trả staleness hint "⚠️ Index is N commit(s) behind HEAD" →
  TỰ chạy \`gitnexus analyze .\` (trong workspace root) trước khi tiếp tục task
- Sau khi \`gitnexus analyze\` xong: **KHÔNG tự chạy \`gitnexus wiki\`**
  (cost cao, có thể block session 2-5 phút) — user sẽ chạy manual khi cần

**Folders Claude scan auto cho skills:**

- \`~/.claude/skills/gitnexus-*/\` — GitNexus global skills (exploring, debugging, ...)
- \`.claude/pack/skills/\` — NAL team-shared skills (qua team-ai-pack)
- Cả 2 đều được scan, không xung đột (different naming prefix)

**Manual wiki update:**

Khi user cần regenerate wiki sau refactor lớn — chạy:

\`\`\`bash
gitnexus wiki . --api-key <key> --base-url <url>
\`\`\`
`;
}

// Build the template-rendering variable bag. lastScan stamps "now" — tests
// that need a deterministic value should freeze time via vi.useFakeTimers().
// v1.4.0: gitnexusReady flag controls render GitNexus section vào CLAUDE.md.
// v3: bỏ `mode` (InitMode internal/client/library dead sau gộp 1 flow).
export function buildScaffoldVariables(args: {
  projectName: string;
  projectDescription: string;
  teamOwner: string;
  packVersion: string;
  gitnexusReady?: boolean;
}): ScaffoldVariables {
  return {
    projectName: args.projectName,
    projectDescription: args.projectDescription,
    teamOwner: args.teamOwner,
    avatarVersion: readCliVersion(),
    packVersion: args.packVersion,
    lastScan: new Date().toISOString(),
    gitnexusSection: buildGitnexusSection(args.gitnexusReady ?? false),
  };
}
